import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Calendar as CalendarIcon, Filter, History, X, Share2,
  Camera, Loader2, CheckCircle2, ImageIcon, Search, ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import type { SessionsFilter, SessionsViewProps } from "@/ui/view-props";
import { FullScreenImageViewer } from "@/components/FullScreenImageViewer";

const FILTERS: { label: string; value: SessionsFilter }[] = [
  { label: "All", value: "all" },
  { label: "Unsettled", value: "unsettled" },
  { label: "Settled", value: "settled" },
];

function SessionsSkeleton() {
  return (
    <div className="space-y-2 animate-fade-in">
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}

export function SessionsModernView(p: SessionsViewProps) {
  return (
    <div className="space-y-5 py-1 animate-fade-in">

      {/* ── KPI strip ──────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Unsettled", value: p.stats.unsettled },
          { label: "Total", value: p.stats.totalSessions },
          { label: "Players", value: p.stats.players },
        ].map(({ label, value }) => (
          <Card key={label}>
            <CardContent className="p-3 text-center">
              <div className="section-label mb-1">{label}</div>
              <div className="text-2xl font-bold font-mono tabular-nums">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Search bar ─────────────────────────────────────── */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60 pointer-events-none" />
        <input
          className={cn(
            "w-full pl-10 pr-4 py-2.5 text-sm rounded-xl",
            "bg-muted/50 border border-transparent",
            "placeholder:text-muted-foreground/50",
            "focus:outline-none focus:ring-2 focus:ring-ring/60 focus:border-ring/30 focus:bg-background",
            "transition-all duration-150",
          )}
          placeholder="Search by player or date…"
          value={p.searchQuery}
          onChange={(e) => p.setSearchQuery(e.target.value)}
        />
        {p.searchQuery && (
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground transition-colors"
            onClick={() => p.setSearchQuery("")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
          <div className="flex gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => p.setFilter(f.value)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150",
                  p.filter === f.value
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date filter */}
        {p.settings.session_calendar_search && (
          <div className="flex items-center gap-1.5">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={p.selectedDate ? "default" : "outline"}
                  size="sm"
                  className={cn("gap-1.5 h-8 text-xs rounded-full", !p.selectedDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {p.selectedDate ? format(p.selectedDate, "MMM d, yyyy") : "By date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={p.selectedDate}
                  onSelect={p.setSelectedDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {p.selectedDate && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => p.setSelectedDate(undefined)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Session list ───────────────────────────────────── */}
      {p.loading ? (
        <SessionsSkeleton />
      ) : p.sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
            <History className="h-7 w-7 text-muted-foreground/50" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">No sessions found</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {p.searchQuery ? "Try a different search" : "Start your first session"}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2 animate-slide-up">
          {p.sessions.map((s) => (
            <Card key={s.id} className="hover:shadow-card-md transition-all duration-150 group overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-stretch">
                  {/* Left accent strip */}
                  <div className={cn(
                    "w-1 shrink-0 rounded-l-xl",
                    s.status === "settled" ? "bg-success/40" : "bg-primary/50",
                  )} />

                  <div className="flex-1 p-3 flex items-center justify-between gap-3 min-w-0">
                    <Link
                      to={p.isReadOnly ? `/public/${p.targetUserId}/session/${s.id}` : `/session/${s.id}`}
                      className="flex-1 min-w-0"
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <div className="text-sm font-semibold truncate">
                        {format(new Date(s.date), "MMMM d, yyyy")}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {s.session_players?.length || 0} players ·{" "}
                        {format(new Date(s.date), "h:mm a")}
                      </div>
                    </Link>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Score sheet thumbnail */}
                      {p.scoreSheetUrls?.[s.id] && (
                        <FullScreenImageViewer
                          src={p.scoreSheetUrls[s.id]}
                          trigger={
                            <button className="h-7 w-7 rounded-md overflow-hidden border border-border/60 hover:border-border transition-colors">
                              <img
                                src={p.scoreSheetUrls[s.id]}
                                alt="thumbnail"
                                className="h-full w-full object-cover"
                              />
                            </button>
                          }
                        />
                      )}

                      {/* Status badge */}
                      <Badge variant={s.status === "settled" ? "success" : "default"}>
                        {s.status === "settled" ? "Settled" : "Open"}
                      </Badge>

                      {/* Upload scoresheet */}
                      {!p.isReadOnly && (
                        <div className="flex items-center">
                          <input type="file" accept="image/*" capture="environment" id={`camera-${s.id}`} className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadScoreSheet(s.id, f); }} />
                          <input type="file" accept="image/*" id={`gallery-${s.id}`} className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadScoreSheet(s.id, f); }} />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 transition-colors"
                                disabled={p.isUploading === s.id}
                              >
                                {p.isUploading === s.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : s.score_sheet_path ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                                ) : (
                                  <Camera className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => document.getElementById(`camera-${s.id}`)?.click()}>
                                <Camera className="mr-2 h-4 w-4" /><span>Take Photo</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => document.getElementById(`gallery-${s.id}`)?.click()}>
                                <ImageIcon className="mr-2 h-4 w-4" /><span>Photo Library</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}

                      {/* Share */}
                      <button
                        className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 transition-colors"
                        title="Share on WhatsApp"
                        onClick={(e) => {
                          e.preventDefault();
                          const dateStr = format(new Date(s.date), "MMM d, yyyy");
                          const resultsSummary = [...(s.session_players || [])]
                            .sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))
                            .map((sp, i) => `${i + 1}. ${sp.players?.name}: ${sp.net_amount >= 0 ? "+" : ""}${Math.round(sp.net_amount)}`)
                            .join("\n");
                          const prefix = p.isReadOnly ? `/public/${p.targetUserId}` : "";
                          const url = `${window.location.origin}${prefix}/session/${s.id}`;
                          const text = `🃏 SSC Session — ${dateStr}\n\n${resultsSummary}\n\nFull results: ${url}`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                        }}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                      </button>

                      <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
